/**
 * Helper class to generate sounds like ringing, dtmf, dialtones etc.
 * using Web Audio API.
 */
import debug from 'debug'
import BrowserMedia from 'Store/sipua/browser_media'
import EmbeddedLocalStorage from '@/api/embedded'
const dbg = debug('class:sounds:')
const ACTX = Symbol('audio context')
const FRQHI = Symbol('high frequency channel')
const FRQLO = Symbol('low frequency channel')
const NODEGAIN = Symbol('gain node from audio context')

const CREATE_ACTX = Symbol('private func to create audio context')
const PLAY_START = Symbol('private func to start audio tones play')
const PLAY_STOP = Symbol('private func to stop audio tones play')

const RINGING_BASE64 = '/ringtones/Ring8.mp3'
const CALLPROGRESS_BASE64 = '/ringback.mp3'
const DBLBEEP_BASE64 = '/doublebip.mp3'

// DTMF table with low and high frequencies in Hz
// https://en.wikipedia.org/wiki/Dual-tone_multi-frequency_signaling#Keypad
const DTMF = {
  //    low Hz , high Hz
  1: { l: 1209, h: 697 },
  2: { l: 1336, h: 697 },
  3: { l: 1477, h: 697 },
  4: { l: 1209, h: 770 },
  5: { l: 1336, h: 770 },
  6: { l: 1477, h: 770 },
  7: { l: 1209, h: 852 },
  8: { l: 1336, h: 852 },
  9: { l: 1477, h: 852 },
  '*': { l: 1209, h: 941 },
  0: { l: 1336, h: 941 },
  '#': { l: 1477, h: 941 }
}

const embedded = EmbeddedLocalStorage.get()

class Sounds {
  constructor () {
    this.gainValue = 0.20
    this.filterType = 'lowpass'
    this.filterFreq = 8000
    this.DTMFLen = 0.20
    this.player = {
      ringing: this.createPlayer(RINGING_BASE64),
      callprogress: this.createPlayer(CALLPROGRESS_BASE64),
      doubleBeep: this.createPlayer(DBLBEEP_BASE64)
    }
    this.canPlayAudio = false
  }

  createPlayer (src) {
    const player = new Audio(src)
    player.muted = true
    player.loop = true
    player.oncanplaythrough = () => { this.canPlayAudio = true }
    return player
  }

  DTMF (key) {
    if (embedded) return

    if (undefined === DTMF[key]) {
      dbg(`Invalid DTMF key ${key}`)
      return
    }
    const frq = DTMF[key]
    this.gainValue = 0.20
    this[CREATE_ACTX]()

    this[FRQHI].frequency.value = frq.l
    this[FRQLO].frequency.value = frq.h

    this[PLAY_START]()

    this[PLAY_STOP](this.DTMFLen)
  }

  /**
   * Call progress sounds
   */
  async startCallProgress (deviceId, onCall = false) {
    if (embedded) return false

    if (!this.canPlayAudio) {
      dbg('Can not play call progress. Audio not ready.')
      return false
    }

    const player = this.player.callprogress
    dbg(`start call progress sound when user is ${onCall ? 'on call' : 'not on call'}`)
    player.muted = false
    const sinkId = deviceId || 'default'
    player.currentTime = 0.0

    if (BrowserMedia.HasSupport() && navigator.mediaDevices?.enumerateDevices) {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices()
        const outputDevices = devices.filter(d => d.kind === 'audiooutput')
        const valid = outputDevices.find(d => d.deviceId === sinkId)

        if (valid) {
          await player.setSinkId(sinkId)
          dbg(`Browser supports setSinkId. Set output device to: ${sinkId}`)
        } else {
          dbg(`Invalid sinkId: ${sinkId}. Skipping setSinkId`)
        }
      } catch (err) {
        dbg('setSinkId error: %o', err)
      }
    }

    player.play().catch(e => dbg('startCallProgress play error: %o', e))
  }

  stopCallProgress () {
    this.player.callprogress.pause()
  }

  /**
   * Ring tones for incoming and outgoing calls.
   * Double beep.
   */
  startRinging (deviceId, onCall = false) {
    if (embedded) return false

    if (!this.canPlayAudio) {
      dbg('Can not play audio. Not ready.')
      return false
    }
    const player = onCall ? this.player.doubleBeep : this.player.ringing
    dbg(`start ringing when user is ${onCall ? 'on call' : 'not on call'}`)
    player.muted = false
    const sinkId = deviceId || 'default'
    dbg(`startRinging using sinkId '${sinkId}' with deviceId '${deviceId}'`)
    player.currentTime = 0.0
    if (BrowserMedia.HasSupport()) {
      dbg(`Browser has support for special Web audio API. Set output device to: ${sinkId}`)
      player.setSinkId(sinkId)
    }
    player.volume = 0.6
    player.play().catch(e => dbg('startRinging play error: %o', e))
  }

  stopRinging () {
    this.player.ringing.pause()
    this.player.doubleBeep.pause()
  }

  /**
   * Start playing both channels
   */
  [PLAY_START] () {
    if (embedded) return
    this[FRQHI].start(0)
    this[FRQLO].start(0)
  }

  /**
   * Stop playing both channels.
   * @param {Number} Lenght in seconds from now when to stop
   */
  [PLAY_STOP] (sec) {
    const len = sec
      ? this[ACTX].currentTime + sec
      : 0
    if (this[FRQHI]) this[FRQHI].stop(len)
    if (this[FRQLO]) this[FRQLO].stop(len)
  }

  /**
   * Private function to prepare audio context before play sounds.
   */
  [CREATE_ACTX] () {
    if (embedded) return

    // create audio context
    // adaptor x-browser alias is in application.js
    const context = new AudioContext()
    // low/high frequency
    const frqhi = context.createOscillator()
    const frqlo = context.createOscillator()
    // gain node
    const node = context.createGain()
    node.gain.value = this.gainValue

    const filter = context.createBiquadFilter()
    filter.type = this.filterType
    filter.frequency.setValueAtTime(this.filterFreq, context.currentTime)

    frqhi.connect(node)
    frqlo.connect(node)
    node.connect(filter)
    filter.connect(context.destination)

    this[NODEGAIN] = node
    this[FRQHI] = frqhi
    this[FRQLO] = frqlo
    this[ACTX] = context
  }
}

export default Sounds
